import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Upload, X } from 'lucide-react';
import notificationIcon from '../../shared/assets/icons/iconstack.io - (Notification Line).png';
import cameraAddIcon from '../../shared/assets/icons/iconstack.io - (Ic Fluent Camera Add 24 Regular).png';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import styles from './AddGoalModal.module.css';
import { useIntl } from 'react-intl';

import {
	createFinancialGoal,
	uploadGoalPicture,
} from '../../modules/Goals/api/index';

// Custom Input component for DatePicker
const CustomDateInput = React.forwardRef(
	({ value, onClick, onChange, error, success, placeholder }, ref) => (
		<div
			className={`${styles.customDateInput} ${
				error ? styles.inputError : success ? styles.inputSuccess : ''
			}`}
			onClick={onClick}
			ref={ref}
		>
			<span className={value ? styles.selectedDate : styles.placeholder}>
				{value || placeholder || 'Select date'}
			</span>
		</div>
	),
);

CustomDateInput.displayName = 'CustomDateInput';

const REMIND_DAYS_WEEKLY = 7;
const REMIND_DAYS_MONTHLY = 30;

const AddGoalModal = ({ open, onClose, onGoalAdded }) => {
	const intl = useIntl();
	const [goalType, setGoalType] = useState('Primary');
	const [goalTitle, setGoalTitle] = useState('');
	const [startDate, setStartDate] = useState(new Date());
	const [dueDate, setDueDate] = useState(new Date());
	const [amount, setAmount] = useState('');
	const [currency, setCurrency] = useState('USD');
	const [notify, setNotify] = useState(false);
	const [remindFrequency, setRemindFrequency] = useState(
		intl.formatMessage({ id: 'AddRecordModalID24' }),
	);
	const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false);
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({});
	const [fieldsHadError, setFieldsHadError] = useState({});
	const [imagePreview, setImagePreview] = useState(null);
	const [imageFile, setImageFile] = useState(null);
	const [uploadingImage, setUploadingImage] = useState(false);

	const frequencies = [
		intl.formatMessage({ id: 'AddRecordModalID25' }),
		intl.formatMessage({ id: 'AddRecordModalID24' }),
	];

	const frequencyToRemindDays = freq => {
		const weeklyMsg = intl.formatMessage({ id: 'AddRecordModalID25' });
		const monthlyMsg = intl.formatMessage({ id: 'AddRecordModalID24' });
		if (freq === weeklyMsg) return REMIND_DAYS_WEEKLY;
		if (freq === monthlyMsg) return REMIND_DAYS_MONTHLY;
		return REMIND_DAYS_MONTHLY;
	};

	// Refs for dropdowns
	const startDateRef = useRef(null);
	const dueDateRef = useRef(null);
	const frequencyRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = event => {
			if (
				frequencyRef.current &&
				!frequencyRef.current.contains(event.target)
			) {
				setShowFrequencyDropdown(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const validateTitle = title => {
		const trimmedTitle = title.trim();
		if (!trimmedTitle) {
			return intl.formatMessage({ id: 'AddGoalModalErrorID1' });
		}
		if (trimmedTitle.length > 255) {
			return intl.formatMessage({ id: 'AddGoalModalErrorID2' });
		}
		return null;
	};

	const validateAmount = amountValue => {
		const amountNum = parseFloat(amountValue);
		if (!amountValue || isNaN(amountNum)) {
			return intl.formatMessage({ id: 'AddGoalModalErrorID4' });
		}
		if (amountNum <= 0) {
			return intl.formatMessage({ id: 'AddGoalModalErrorID5' });
		}
		return null;
	};

	const validateStartDate = date => {
		if (!date) {
			return intl.formatMessage({ id: 'AddGoalModalErrorID6' });
		}
		return null;
	};

	const validateDueDate = (due, start) => {
		if (!due) {
			return intl.formatMessage({ id: 'AddGoalModalErrorID7' });
		}
		if (start && due < start) {
			return intl.formatMessage({ id: 'AddGoalModalErrorID8' });
		}
		return null;
	};

	const getFieldClassName = (field, baseClass) => {
		const hasError = !!errors[field];
		const hasSuccess = fieldsHadError[field] && !hasError;
		return `${baseClass} ${hasError ? styles.inputError : ''} ${
			hasSuccess ? styles.inputSuccess : ''
		}`.trim();
	};

	const validateForm = () => {
		const newErrors = {};
		const titleError = validateTitle(goalTitle);
		if (titleError) newErrors.title = titleError;

		const amountError = validateAmount(amount);
		if (amountError) newErrors.amount = amountError;

		const startDateError = validateStartDate(startDate);
		if (startDateError) newErrors.startDate = startDateError;

		const dueDateError = validateDueDate(dueDate, startDate);
		if (dueDateError) newErrors.dueDate = dueDateError;

		setErrors(newErrors);
		if (Object.keys(newErrors).length > 0) {
			setFieldsHadError(prev => ({
				...prev,
				...Object.fromEntries(Object.keys(newErrors).map(key => [key, true])),
			}));
		}
		return Object.keys(newErrors).length === 0;
	};

	useEffect(() => {
		if (!Object.values(fieldsHadError).some(Boolean)) return;

		setErrors(prev => {
			const next = { ...prev };

			if (fieldsHadError.title) {
				const titleError = validateTitle(goalTitle);
				if (titleError) next.title = titleError;
				else delete next.title;
			}

			if (fieldsHadError.amount) {
				const amountError = validateAmount(amount);
				if (amountError) next.amount = amountError;
				else delete next.amount;
			}

			if (fieldsHadError.startDate) {
				const startDateError = validateStartDate(startDate);
				if (startDateError) next.startDate = startDateError;
				else delete next.startDate;
			}

			if (fieldsHadError.dueDate) {
				const dueDateError = validateDueDate(dueDate, startDate);
				if (dueDateError) next.dueDate = dueDateError;
				else delete next.dueDate;
			}

			return next;
		});
	}, [goalTitle, amount, startDate, dueDate, fieldsHadError]);

	const handleImageUpload = async e => {
		const file = e.target.files[0];
		if (!file) return;

		// Validate file type
		const validTypes = [
			'image/jpeg',
			'image/jpg',
			'image/png',
			'image/gif',
			'image/webp',
		];
		if (!validTypes.includes(file.type)) {
			alert(intl.formatMessage({ id: 'AddGoalModalErrorID10' }));
			return;
		}

		// Validate file size (5MB max)
		if (file.size > 5 * 1024 * 1024) {
			alert(intl.formatMessage({ id: 'AddGoalModalErrorID11' }));
			return;
		}

		setImageFile(file);
		const reader = new FileReader();
		reader.onloadend = () => {
			setImagePreview(reader.result);
		};
		reader.readAsDataURL(file);
	};

	const handleAddGoal = async () => {
		if (!validateForm()) {
			return;
		}

		setLoading(true);

		try {
			const goalData = {
				title: goalTitle.trim(),
				target_amount: parseFloat(amount),
				currency: currency,
				start_date: format(startDate, 'yyyy-MM-dd'),
				due_date: format(dueDate, 'yyyy-MM-dd'),
				remind_me_in_days: notify ? frequencyToRemindDays(remindFrequency) : 0,
				is_default: goalType === 'Primary',
			};

			const responseData = await createFinancialGoal(goalData);

			let createdGoal;
			if (responseData.goal) {
				createdGoal = responseData.goal;
			} else if (responseData.data) {
				createdGoal = responseData.data;
			} else {
				createdGoal = responseData;
			}

			// Upload image if selected
			if (imageFile && createdGoal.id) {
				setUploadingImage(true);
				try {
					const goalId = createdGoal.id || createdGoal._id;
					await uploadGoalPicture(goalId, imageFile);
				} catch (imageError) {
					console.error('Error uploading image:', imageError);
					// Don't fail the whole creation if image upload fails
					alert(
						imageError.message ||
							intl.formatMessage({ id: 'AddGoalModalErrorID12' }),
					);
				} finally {
					setUploadingImage(false);
				}
			}

			if (onGoalAdded) {
				onGoalAdded(createdGoal);
			}
			resetForm();
			onClose();
		} catch (error) {
			console.error('Error creating goal:', error);
			alert(
				error.message || intl.formatMessage({ id: 'AddGoalModalErrorID13' }),
			);
		} finally {
			setLoading(false);
		}
	};

	const resetForm = () => {
		setGoalType('Primary');
		setGoalTitle('');
		setStartDate(new Date());
		setDueDate(new Date());
		setAmount('');
		setCurrency('USD');
		setNotify(false);
		setRemindFrequency(intl.formatMessage({ id: 'AddRecordModalID24' }));
		setShowFrequencyDropdown(false);
		setErrors({});
		setFieldsHadError({});
		setImagePreview(null);
		setImageFile(null);
	};

	const handleCancel = () => {
		resetForm();
		onClose();
	};

	// Close dropdowns when clicking outside
	useEffect(() => {
		const handleClickOutside = event => {
			if (
				startDateRef.current &&
				!startDateRef.current.contains(event.target)
			) {
				// Handle if needed
			}
			if (dueDateRef.current && !dueDateRef.current.contains(event.target)) {
				// Handle if needed
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	if (!open) return null;

	return (
		<div className={styles.overlay}>
			<div className={styles.modal}>
				<button className={styles.closeButton} onClick={handleCancel}>
					<X size={24} />
				</button>

				<h2 className={styles.title}>
					{intl.formatMessage({ id: 'AddGoalModalID1' })}
				</h2>

				<div className={styles.modalContent}>
					<div className={styles.typeToggle}>
						<button
							className={`${styles.typeButton} ${
								goalType === 'Primary' ? styles.activeType : ''
							}`}
							onClick={() => setGoalType('Primary')}
						>
							{intl.formatMessage({ id: 'AddGoalModalID2' })}
						</button>
						<button
							className={`${styles.typeButton} ${
								goalType === 'Secondary' ? styles.activeType : ''
							}`}
							onClick={() => setGoalType('Secondary')}
						>
							{intl.formatMessage({ id: 'AddGoalModalID3' })}
						</button>
					</div>

					<div className={styles.formGroup}>
						<label className={styles.label}>
							{intl.formatMessage({ id: 'AddGoalModalID4' })}
						</label>
						<div className={styles.inputWithIcon}>
							<input
								type='text'
								value={goalTitle}
								onChange={e => setGoalTitle(e.target.value)}
								className={getFieldClassName('title', styles.input)}
								placeholder={intl.formatMessage({ id: 'AddGoalModalID5' })}
								maxLength={255}
							/>
							{/* <Smile size={20} className={styles.inputIcon} /> */}
						</div>
						{errors.title && (
							<span className={styles.errorText}> * {errors.title}</span>
						)}
					</div>

					<div className={styles.formGroup}>
						<label className={styles.label}>
							{intl.formatMessage({ id: 'AddGoalModalID6' })}
						</label>
						{imagePreview ? (
							<div className={styles.imagePreviewContainer}>
								<img
									src={imagePreview}
									alt='Goal preview'
									className={styles.imagePreview}
								/>
								<div className={styles.imageActions}>
									<label className={styles.imageUploadButton}>
										<Upload size={16} />
										{intl.formatMessage({ id: 'AddGoalModalID7' })}
										<input
											type='file'
											accept='image/jpeg,image/jpg,image/png,image/gif,image/webp'
											onChange={handleImageUpload}
											style={{ display: 'none' }}
											disabled={uploadingImage}
										/>
									</label>
									<button
										className={styles.imageRemoveButton}
										onClick={() => {
											setImagePreview(null);
											setImageFile(null);
										}}
										disabled={uploadingImage}
										type='button'
									>
										<X size={16} />
									</button>
								</div>
							</div>
						) : (
							<label className={styles.imageInputField}>
								<span className={styles.imagePlaceholder}>
									{intl.formatMessage({ id: 'AddGoalModalID23' })}
								</span>
								<img
									src={cameraAddIcon}
									alt=''
									className={styles.imageFieldIcon}
									aria-hidden='true'
								/>
								<input
									type='file'
									accept='image/jpeg,image/jpg,image/png,image/gif,image/webp'
									onChange={handleImageUpload}
									className={styles.imageFileInput}
									disabled={uploadingImage}
								/>
							</label>
						)}
						{uploadingImage && (
							<p className={styles.uploadStatus}>
								{intl.formatMessage({ id: 'AddGoalModalID9' })}
							</p>
						)}
					</div>

					<div className={styles.dateRow}>
						<label className={`${styles.label} ${styles.dateStartLabel}`}>
							{intl.formatMessage({ id: 'AddGoalModalID11' })}
						</label>
						<label className={`${styles.label} ${styles.dateDueLabel}`}>
							{intl.formatMessage({ id: 'AddGoalModalID12' })}
						</label>
						<div className={styles.dateStartField} ref={startDateRef}>
							<DatePicker
								selected={startDate}
								onChange={date => setStartDate(date)}
								dateFormat='MM/dd/yyyy'
								customInput={
									<CustomDateInput
										error={errors.startDate}
										success={fieldsHadError.startDate && !errors.startDate}
										placeholder={intl.formatMessage({ id: 'AddGoalModalID20' })}
									/>
								}
								popperClassName={styles.datePickerPopper}
								wrapperClassName={styles.datePickerWrapper}
								calendarClassName={styles.datePickerCalendar}
								showPopperArrow={false}
								popperPlacement='bottom-start'
								minDate={new Date()}
								maxDate={dueDate || null}
								todayButton='Today'
								showMonthDropdown
								showYearDropdown
								dropdownMode='scroll'
								scrollableYearDropdown
								yearDropdownItemNumber={15}
								isClearable
								onClear={() => setStartDate(new Date())}
								clearButtonTitle='Clear'
								className={getFieldClassName(
									'startDate',
									styles.datePickerInput,
								)}
							/>
						</div>
						<div className={styles.dateArrowCell} aria-hidden='true'>
							<span className={styles.dateArrow}>→</span>
						</div>
						<div className={styles.dateDueField} ref={dueDateRef}>
							<DatePicker
								selected={dueDate}
								onChange={date => setDueDate(date)}
								dateFormat='MM/dd/yyyy'
								customInput={
									<CustomDateInput
										error={errors.dueDate}
										success={fieldsHadError.dueDate && !errors.dueDate}
										placeholder={intl.formatMessage({ id: 'AddGoalModalID21' })}
									/>
								}
								popperClassName={styles.datePickerPopper}
								wrapperClassName={styles.datePickerWrapper}
								calendarClassName={styles.datePickerCalendar}
								showPopperArrow={false}
								popperPlacement='bottom-start'
								minDate={startDate || new Date()}
								todayButton='Today'
								showMonthDropdown
								showYearDropdown
								dropdownMode='scroll'
								scrollableYearDropdown
								yearDropdownItemNumber={15}
								isClearable
								onClear={() => setDueDate(new Date())}
								clearButtonTitle='Clear'
								className={getFieldClassName('dueDate', styles.datePickerInput)}
							/>
						</div>
						{errors.startDate && (
							<span className={`${styles.errorText} ${styles.dateStartError}`}>
								{' '}
								* {errors.startDate}
							</span>
						)}
						{errors.dueDate && (
							<span className={`${styles.errorText} ${styles.dateDueError}`}>
								{' '}
								* {errors.dueDate}
							</span>
						)}
					</div>

					<div className={styles.amountRow}>
						<div className={styles.formGroup}>
							<label className={styles.label}>
								{intl.formatMessage({ id: 'AddGoalModalID13' })}
							</label>
							<input
								type='number'
								value={amount}
								onChange={e => setAmount(e.target.value)}
								className={getFieldClassName('amount', styles.input)}
								placeholder={intl.formatMessage({ id: 'AddGoalModalID22' })}
								step='0.01'
								min='0.01'
							/>
							{errors.amount && (
								<span className={styles.errorText}> * {errors.amount}</span>
							)}
						</div>
						<div className={styles.formGroup}>
							<label className={styles.label}>
								{intl.formatMessage({ id: 'AddGoalModalID14' })}
							</label>
							<div className={styles.currencySelector}>
								<select
									value={currency}
									onChange={e => setCurrency(e.target.value)}
									className={styles.currencySelect}
								>
									<option value='USD'>USD</option>
									<option value='EUR'>EUR</option>
									<option value='AMD'>AMD</option>
									<option value='RUB'>RUB</option>
								</select>
								<ChevronDown size={18} className={styles.currencyChevron} />
							</div>
						</div>
					</div>

					<div className={styles.remindRow}>
						<img
							src={notificationIcon}
							alt=''
							className={styles.bellIcon}
							aria-hidden='true'
						/>
						<div className={styles.checkboxWrapper}>
							<input
								type='checkbox'
								id='goal-notify-checkbox'
								checked={notify}
								onChange={e => setNotify(e.target.checked)}
								className={styles.checkbox}
							/>
							<span className={styles.checkmark} aria-hidden='true' />
						</div>
						<label
							htmlFor='goal-notify-checkbox'
							className={styles.remindLabel}
						>
							{intl.formatMessage({ id: 'AddGoalModalID16' })}
						</label>
						{notify && (
							<div className={styles.dropdownContainer} ref={frequencyRef}>
								<div
									className={styles.frequencyRow}
									onClick={() =>
										setShowFrequencyDropdown(!showFrequencyDropdown)
									}
								>
									<span className={styles.frequencyText}>
										{remindFrequency}
									</span>
									<ChevronDown size={18} />
								</div>
								{showFrequencyDropdown && (
									<div className={styles.dropdownMenu}>
										{frequencies.map(freq => (
											<div
												key={freq}
												className={styles.dropdownItem}
												onClick={() => {
													setRemindFrequency(freq);
													setShowFrequencyDropdown(false);
												}}
											>
												{freq}
											</div>
										))}
									</div>
								)}
							</div>
						)}
					</div>

					<div className={styles.buttonRow}>
						<button
							className={styles.addButton}
							onClick={handleAddGoal}
							disabled={loading}
						>
							{loading
								? intl.formatMessage({ id: 'AddGoalModalID17' })
								: intl.formatMessage({ id: 'AddGoalModalID18' })}
						</button>

						<button
							className={styles.cancelButton}
							onClick={handleCancel}
							disabled={loading}
						>
							{intl.formatMessage({ id: 'AddGoalModalID19' })}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AddGoalModal;
